import { useState, useEffect, useMemo } from "react";
import { db } from "../firebase/config";
import {
  collection, doc, getDocs, addDoc, updateDoc, deleteDoc, serverTimestamp
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";

// ══════════════════════════════════════════════════════════════
// This single file replaces: BirthdayFestivalPopup.jsx,
// FestivalsScreen.jsx, and FloatingFestivalButton.jsx.
// It covers three jobs together:
//   1. Automatic "Happy Birthday / Happy <Festival>" popup, once a day
//   2. A floating calendar button (bottom-left, hidden for Watchman)
//   3. A Month-view calendar (like a phone's Calendar app) showing
//      festivals and birthdays as a dot on the date; MC members can
//      add/edit/delete festivals by tapping a date.
// ══════════════════════════════════════════════════════════════

const FESTIVALS_COLLECTION = "festivals";
const STORAGE_KEY_PREFIX = "abc_wishes_shown_";
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ── small date helpers ──────────────────────────────────────────
function todayParts() {
  const d = new Date();
  return { day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear(), dateStr: d.toISOString().split("T")[0] };
}

// Birthdays are stored as dd-mm-yyyy strings (e.g. "07-11-1990").
function parseDob(dob) {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec((dob || "").trim());
  if (!m) return null;
  return { day: +m[1], month: +m[2], year: +m[3] };
}

function daysInMonth(month, year) {
  return new Date(year, month, 0).getDate();
}

function buildMonthGrid(month, year) {
  const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0 = Sun
  const total = daysInMonth(month, year);
  const grid = [];
  for (let i = 0; i < firstWeekday; i++) grid.push(null);
  for (let d = 1; d <= total; d++) grid.push(d);
  return grid;
}

function isToday(day, month, year) {
  const t = new Date();
  return day === t.getDate() && month === t.getMonth() + 1 && year === t.getFullYear();
}

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════
export default function FestivalCalendar() {
  const { user, isMC } = useAuth();
  const canEdit = isMC() && user?.role !== "Adyantam";
  const isWatchman = user?.role === "Watchman";

  const [festivals, setFestivals] = useState([]);
  const [birthdays, setBirthdays] = useState([]); // { day, month, label }
  const [loaded, setLoaded] = useState(false);

  // Automatic daily popup
  const [popupItems, setPopupItems] = useState(null);
  const [popupDismissed, setPopupDismissed] = useState(false);

  // Month-view modal (opened via floating button)
  const [modalOpen, setModalOpen] = useState(false);
  const today = todayParts();
  const [viewMonth, setViewMonth] = useState(today.month);
  const [viewYear, setViewYear] = useState(today.year);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const festSnap = await getDocs(collection(db, FESTIVALS_COLLECTION));
      setFestivals(festSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const usersSnap = await getDocs(collection(db, "users"));
      const b = [];
      usersSnap.docs.forEach(d => {
        const u = d.data();

        // Family members' birthdays (existing behaviour)
        (u.familyMembers || []).forEach(m => {
          const parsed = parseDob(m.dob);
          if (parsed) b.push({ day: parsed.day, month: parsed.month, label: `${m.name} (${m.relation})` });
        });

        // The logged-in person's own birthday (new, optional field on their
        // own user record). Skipped for Watchman, same as the rest of this
        // feature — that field simply won't be shown to them to fill in.
        if (u.role !== "Watchman") {
          const ownParsed = parseDob(u.dob);
          if (ownParsed) {
            const roleLabel = u.memberType === "owner" ? "Owner"
              : u.memberType === "tenant" ? "Tenant"
              : (u.role || "");
            b.push({ day: ownParsed.day, month: ownParsed.month, label: `${u.name || "Member"} (${roleLabel})` });
          }
        }
      });
      setBirthdays(b);
    } catch (e) {
      console.error("Failed to load calendar data:", e);
    } finally {
      setLoaded(true);
    }
  }

  // Check today's wishes once data is ready
  useEffect(() => {
    if (!loaded) return;
    const storageKey = STORAGE_KEY_PREFIX + today.dateStr;
    if (localStorage.getItem(storageKey)) { setPopupItems([]); return; }

    const found = [];
    festivals.forEach(f => {
      if (f.day === today.day && f.month === today.month) {
        if (f.type === "fixed" || (f.type === "moving" && f.year === today.year)) {
          found.push({ type: "festival", label: f.name });
        }
      }
    });
    birthdays.forEach(b => {
      if (b.day === today.day && b.month === today.month) {
        found.push({ type: "birthday", label: b.label });
      }
    });

    localStorage.setItem(storageKey, "1");
    setPopupItems(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  // Map of day -> items, for the month currently being viewed
  const dataByDay = useMemo(() => {
    const map = {};
    festivals.forEach(f => {
      if (f.month !== viewMonth) return;
      if (f.type === "moving" && f.year !== viewYear) return;
      if (!map[f.day]) map[f.day] = [];
      map[f.day].push({ type: "festival", label: f.name, raw: f });
    });
    birthdays.forEach(b => {
      if (b.month !== viewMonth) return;
      if (!map[b.day]) map[b.day] = [];
      map[b.day].push({ type: "birthday", label: b.label });
    });
    return map;
  }, [festivals, birthdays, viewMonth, viewYear]);

  function prevMonth() {
    setSelectedDay(null); setShowForm(false); setEditing(null);
    if (viewMonth === 1) { setViewMonth(12); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    setSelectedDay(null); setShowForm(false); setEditing(null);
    if (viewMonth === 12) { setViewMonth(1); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }
  function closeModal() {
    setModalOpen(false); setSelectedDay(null); setShowForm(false); setEditing(null); setFormError("");
  }

  async function handleSaveFestival(values) {
    setSaving(true); setFormError("");
    try {
      if (editing?.id) {
        await updateDoc(doc(db, FESTIVALS_COLLECTION, editing.id), {
          ...values, updatedAt: serverTimestamp(), updatedBy: user?.name || user?.role || "MC",
        });
      } else {
        await addDoc(collection(db, FESTIVALS_COLLECTION), {
          ...values, updatedAt: serverTimestamp(), updatedBy: user?.name || user?.role || "MC",
        });
      }
      setShowForm(false); setEditing(null);
      await loadData();
    } catch (e) {
      setFormError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteFestival(f) {
    if (!window.confirm(`Delete "${f.name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, FESTIVALS_COLLECTION, f.id));
      setSelectedDay(null);
      await loadData();
    } catch (e) {
      alert("Delete failed. Please try again.");
    }
  }

  const showAutoPopup = popupItems && popupItems.length > 0 && !popupDismissed;
  const grid = buildMonthGrid(viewMonth, viewYear);

  return (
    <>
      {showAutoPopup && (
        <AutoWishesPopup items={popupItems} onClose={() => setPopupDismissed(true)} />
      )}

      {!isWatchman && (
        <button
          onClick={() => setModalOpen(true)}
          title="Festivals & Birthdays"
          style={{
            position: "fixed", bottom: "20px", left: "20px", zIndex: 400,
            width: "52px", height: "52px", borderRadius: "50%",
            background: "#6c63ff", border: "none", color: "#fff",
            fontSize: "22px", cursor: "pointer",
            boxShadow: "0 4px 14px #6c63ff55",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >📅</button>
      )}

      {modalOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "#000000cc", zIndex: 600,
            display: "flex", alignItems: "flex-end", justifyContent: "flex-start", padding: "16px",
          }}
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div style={{
            background: "#141420", border: "1px solid #2e2e40", borderRadius: "16px",
            width: "100%", maxWidth: "480px", maxHeight: "85vh", overflowY: "auto", padding: "18px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>🎉 Festivals & Birthdays</div>
              <button onClick={closeModal} style={{ background: "transparent", border: "none", color: "#8888a8", fontSize: "20px", cursor: "pointer" }}>✕</button>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <button onClick={prevMonth} style={navBtnStyle}>‹</button>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "#e8e8f0" }}>{MONTH_NAMES[viewMonth - 1]} {viewYear}</div>
              <button onClick={nextMonth} style={navBtnStyle}>›</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: "4px", marginBottom: "4px" }}>
              {DAY_NAMES.map(d => (
                <div key={d} style={{ textAlign: "center", fontSize: "10px", color: "#8888a8", fontWeight: "600" }}>{d}</div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: "4px" }}>
              {grid.map((cell, i) => cell === null ? <div key={i} /> : (
                <button
                  key={i}
                  onClick={() => { setSelectedDay(cell); setShowForm(false); setEditing(null); setFormError(""); }}
                  style={dayCellStyle(cell === selectedDay, !!dataByDay[cell], isToday(cell, viewMonth, viewYear))}
                >
                  {cell}
                  {dataByDay[cell] && (
                    <span style={{
                      position: "absolute", bottom: "4px", left: "50%", transform: "translateX(-50%)",
                      width: "4px", height: "4px", borderRadius: "50%", background: "#6c63ff",
                    }} />
                  )}
                </button>
              ))}
            </div>

            {selectedDay && (
              <div style={{ marginTop: "14px", borderTop: "1px solid #2e2e40", paddingTop: "12px" }}>
                <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginBottom: "8px" }}>
                  {String(selectedDay).padStart(2, "0")} {MONTH_NAMES[viewMonth - 1]} {viewYear}
                </div>

                {(dataByDay[selectedDay] || []).map((item, idx) => (
                  <div key={idx} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    background: "#0f0f13", border: "1px solid #2e2e40", borderRadius: "10px",
                    padding: "10px 12px", marginBottom: "6px", fontSize: "12px", color: "#e8e8f0",
                  }}>
                    <span>{item.type === "birthday" ? "🎂" : "🎉"} {item.label}</span>
                    {item.type === "festival" && canEdit && (
                      <span style={{ display: "flex", gap: "6px" }}>
                        <button onClick={() => { setEditing(item.raw); setShowForm(true); }} style={smallBtnStyle("#6c63ff")}>Edit</button>
                        <button onClick={() => handleDeleteFestival(item.raw)} style={smallBtnStyle("#ff5252")}>Delete</button>
                      </span>
                    )}
                  </div>
                ))}

                {(!dataByDay[selectedDay] || dataByDay[selectedDay].length === 0) && (
                  <div style={{ fontSize: "12px", color: "#555570", marginBottom: "8px" }}>Nothing on this date.</div>
                )}

                {canEdit && !showForm && (
                  <button
                    onClick={() => { setEditing(null); setShowForm(true); }}
                    style={{
                      width: "100%", background: "#6c63ff15", color: "#6c63ff", border: "1px solid #6c63ff40",
                      borderRadius: "10px", padding: "10px", fontSize: "12px", fontWeight: "600", cursor: "pointer", marginTop: "4px",
                    }}
                  >➕ Add Festival on this date</button>
                )}

                {canEdit && showForm && (
                  <FestivalMiniForm
                    initial={editing || { name: "", type: "fixed", day: selectedDay, month: viewMonth, year: "" }}
                    saving={saving}
                    error={formError}
                    onSave={handleSaveFestival}
                    onCancel={() => { setShowForm(false); setEditing(null); setFormError(""); }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── Automatic daily wishes popup ─────────────────────────────────
function AutoWishesPopup({ items, onClose }) {
  const festivals = items.filter(i => i.type === "festival");
  const birthdays = items.filter(i => i.type === "birthday");

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000cc", zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "#1a1a24", border: "1px solid #6c63ff40", borderRadius: "20px",
        width: "100%", maxWidth: "420px", padding: "28px 22px", textAlign: "center",
      }}>
        <div style={{ fontSize: "40px", marginBottom: "10px" }}>{birthdays.length > 0 ? "🎂" : "🎉"}</div>

        {festivals.length > 0 && (
          <div style={{ marginBottom: birthdays.length > 0 ? "18px" : "0" }}>
            <div style={{ fontSize: "17px", fontWeight: "700", color: "#e8e8f0", marginBottom: "4px" }}>
              Happy {festivals.map(f => f.label).join(" & ")}!
            </div>
            <div style={{ fontSize: "12px", color: "#8888a8" }}>Wishing you and your family a wonderful day.</div>
          </div>
        )}

        {birthdays.length > 0 && (
          <div>
            <div style={{ fontSize: "17px", fontWeight: "700", color: "#e8e8f0", marginBottom: "6px" }}>🎈 Happy Birthday!</div>
            {birthdays.map((b, i) => (
              <div key={i} style={{ fontSize: "13px", color: "#8888a8", marginBottom: "2px" }}>{b.label}</div>
            ))}
          </div>
        )}

        {birthdays.length === 0 && (
          <div style={{ fontSize: "12px", color: "#555570", marginTop: "16px", paddingTop: "14px", borderTop: "1px solid #2e2e40" }}>
            🎂 Haven't added your birthday yet? Add it in your Family tab to get wishes like this!
          </div>
        )}

        <button onClick={onClose} style={{
          marginTop: "20px", width: "100%", background: "#6c63ff", color: "#fff", border: "none",
          borderRadius: "10px", padding: "12px", fontSize: "14px", fontWeight: "600", cursor: "pointer",
        }}>Close</button>
      </div>
    </div>
  );
}

// ── Compact Add/Edit Festival form (used inside the calendar modal) ──
function FestivalMiniForm({ initial, saving, error, onSave, onCancel }) {
  const [f, setF] = useState({
    name: initial.name || "",
    type: initial.type || "fixed",
    day: initial.day || "",
    month: initial.month || 1,
    year: initial.year || "",
  });
  const [localError, setLocalError] = useState("");

  function update(key, value) { setLocalError(""); setF(prev => ({ ...prev, [key]: value })); }

  function handleSave() {
    if (!f.name.trim()) { setLocalError("Festival name is required."); return; }
    const day = Number(f.day), month = Number(f.month);
    if (!day || day < 1 || day > daysInMonth(month, Number(f.year) || 2026)) {
      setLocalError("Please enter a valid day for the selected month."); return;
    }
    if (f.type === "moving" && !f.year) { setLocalError("Moving festivals need this year's year (e.g. 2026)."); return; }

    onSave({
      name: f.name.trim(),
      type: f.type,
      day,
      month,
      year: f.type === "moving" ? Number(f.year) : null,
    });
  }

  const inputStyle = {
    width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "8px", padding: "9px 12px", color: "#e8e8f0",
    fontSize: "13px", outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { display: "block", fontSize: "11px", color: "#8888a8", marginBottom: "4px" };

  return (
    <div style={{ background: "#0f0f13", border: "1px solid #2e2e40", borderRadius: "10px", padding: "12px", marginTop: "8px" }}>
      <div style={{ marginBottom: "10px" }}>
        <label style={labelStyle}>Festival Name *</label>
        <input style={inputStyle} value={f.name} onChange={e => update("name", e.target.value)} placeholder="e.g. Diwali" />
      </div>

      <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
        {[["fixed", "Fixed (repeats)"], ["moving", "Moving (yearly)"]].map(([value, text]) => (
          <button key={value} onClick={() => update("type", value)} style={{
            flex: 1, padding: "8px", borderRadius: "8px", fontSize: "11px", cursor: "pointer",
            border: `1px solid ${f.type === value ? "#6c63ff" : "#2e2e40"}`,
            background: f.type === value ? "#6c63ff15" : "transparent",
            color: f.type === value ? "#6c63ff" : "#8888a8",
          }}>{text}</button>
        ))}
      </div>

      <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Day *</label>
          <input style={inputStyle} value={f.day} inputMode="numeric"
            onChange={e => update("day", e.target.value.replace(/\D/g, "").slice(0, 2))} />
        </div>
        <div style={{ flex: 2 }}>
          <label style={labelStyle}>Month *</label>
          <select style={{ ...inputStyle, cursor: "pointer" }} value={f.month} onChange={e => update("month", e.target.value)}>
            {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </div>

      {f.type === "moving" && (
        <div style={{ marginBottom: "10px" }}>
          <label style={labelStyle}>Year (this year's date) *</label>
          <input style={inputStyle} value={f.year} inputMode="numeric"
            onChange={e => update("year", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="e.g. 2026" />
        </div>
      )}

      {(localError || error) && (
        <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "8px", padding: "8px 12px", fontSize: "12px", color: "#ff5252", marginBottom: "10px" }}>
          ⚠ {localError || error}
        </div>
      )}

      <div style={{ display: "flex", gap: "8px" }}>
        <button onClick={onCancel} style={{
          flex: 1, background: "transparent", color: "#8888a8", border: "1px solid #2e2e40",
          borderRadius: "8px", padding: "10px", fontSize: "12px", fontWeight: "600", cursor: "pointer",
        }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{
          flex: 2, background: "#6c63ff", color: "#fff", border: "none", borderRadius: "8px",
          padding: "10px", fontSize: "13px", fontWeight: "600",
          cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
        }}>{saving ? "Saving…" : "Save Festival"}</button>
      </div>
    </div>
  );
}

// ── shared small styles ──────────────────────────────────────────
const navBtnStyle = {
  background: "#6c63ff15", color: "#6c63ff", border: "1px solid #6c63ff40",
  borderRadius: "8px", width: "32px", height: "32px", fontSize: "16px", cursor: "pointer",
};

function smallBtnStyle(color) {
  return {
    background: `${color}15`, color, border: `1px solid ${color}40`,
    borderRadius: "6px", padding: "4px 9px", fontSize: "10px", cursor: "pointer",
  };
}

function dayCellStyle(selected, hasData, today) {
  return {
    position: "relative", aspectRatio: "1", borderRadius: "8px", fontSize: "12px",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    background: selected ? "#6c63ff" : today ? "#6c63ff20" : "transparent",
    color: selected ? "#fff" : hasData ? "#e8e8f0" : "#8888a8",
    border: today && !selected ? "1px solid #6c63ff60" : "1px solid transparent",
    fontWeight: selected || today ? "700" : "400",
  };
}
